/**
 * CCIR utility functions for participation calculations and group metrics.
 */
import { CcirProperty, CcirOwner, CcirGroupMetrics, BureauErrorCause, BureauErrorDetail } from '../types';

// ── Bureau error status ──

/** Owner bureau processing status derived from the backend error details. */
export const OwnerBureauStatus = {
    OK: 'OK',
    NOT_FOUND: 'NOT_FOUND',
    ERROR: 'ERROR',
} as const satisfies Record<string, string>;

export type OwnerBureauStatus = typeof OwnerBureauStatus[keyof typeof OwnerBureauStatus];

/**
 * Normalized error details for an owner. Prefers the structured
 * `bureauErrorDetails` (new backend); falls back to `bureauErrors` (names only,
 * older backend) treated as real ERRORs since no cause is available.
 */
export const getOwnerErrorDetails = (owner: CcirOwner): BureauErrorDetail[] => {
    if (owner.bureauErrorDetails && owner.bureauErrorDetails.length > 0) {
        return owner.bureauErrorDetails;
    }
    if (owner.bureauErrors && owner.bureauErrors.length > 0) {
        return owner.bureauErrors.map(bureau => ({ bureau, cause: BureauErrorCause.ERROR, message: '' }));
    }
    return [];
};

/**
 * Overall status for an owner: ERROR if any detail is a real failure,
 * else NOT_FOUND if there are only "no record" outcomes, else OK.
 */
export const getOwnerBureauStatus = (owner: CcirOwner): OwnerBureauStatus => {
    const details = getOwnerErrorDetails(owner);
    if (details.length === 0) return OwnerBureauStatus.OK;
    if (details.some(d => d.cause === BureauErrorCause.ERROR)) return OwnerBureauStatus.ERROR;
    return OwnerBureauStatus.NOT_FOUND;
};

/** True when any co-owner has a real ERROR (technical failure), meaning group data is partial. */
export const hasGroupError = (coOwners: CcirOwner[]): boolean =>
    coOwners.some(o => getOwnerBureauStatus(o) === OwnerBureauStatus.ERROR);

/** Sum total area of all properties in hectares. */
export const calcTotalArea = (properties: CcirProperty[]): number =>
    properties.reduce((sum, p) => sum + p.area, 0);

/** Sum total value of all properties. */
export const calcTotalValue = (properties: CcirProperty[]): number =>
    properties.reduce((sum, p) => sum + p.value, 0);

/** Calculate the drawee's consolidated participation across all properties (by area). */
export const calcDraweeParticipation = (properties: CcirProperty[]): number => {
    const totalArea = calcTotalArea(properties);
    if (totalArea === 0) return 0;

    const draweeArea = properties.reduce((sum, prop) => {
        const draweeOwner = prop.owners.find(o => o.isDrawee);
        if (!draweeOwner) return sum;
        return sum + (prop.area * draweeOwner.participationPercent / 100);
    }, 0);

    return (draweeArea / totalArea) * 100;
};

/** Calculate the drawee's patrimony (area × valorHa × participation). */
export const calcDraweePatrimony = (properties: CcirProperty[]): number =>
    properties.reduce((sum, prop) => {
        const draweeOwner = prop.owners.find(o => o.isDrawee);
        if (!draweeOwner) return sum;
        return sum + (prop.area * prop.valorHa * draweeOwner.participationPercent / 100);
    }, 0);

/** Calculate group metrics after deep analysis. */
export const calcGroupMetrics = (coOwners: CcirOwner[], properties: CcirProperty[]): CcirGroupMetrics => {
    const groupPatrimony = calcTotalValue(properties);
    const groupDebt = coOwners.reduce((sum, o) => sum + (o.scrTotal ?? 0), 0);
    const groupPatrimonyDebtRatio = groupDebt > 0 ? groupPatrimony / groupDebt : 0;
    const draweeParticipation = calcDraweeParticipation(properties);
    const draweePatrimony = calcDraweePatrimony(properties);

    return { groupPatrimony, groupDebt, groupPatrimonyDebtRatio, draweeParticipation, draweePatrimony };
};

/** Get consolidated owners across all properties (unique by document, weighted by area). */
export const getConsolidatedOwners = (properties: CcirProperty[]): CcirOwner[] => {
    const totalArea = calcTotalArea(properties);
    if (totalArea === 0) return [];

    const ownerMap = new Map<string, CcirOwner>();

    for (const prop of properties) {
        for (const owner of prop.owners) {
            const existing = ownerMap.get(owner.document);
            const weightedParticipation = (prop.area / totalArea) * owner.participationPercent;

            if (existing) {
                ownerMap.set(owner.document, {
                    ...existing,
                    participationPercent: existing.participationPercent + weightedParticipation,
                });
            } else {
                ownerMap.set(owner.document, {
                    ...owner,
                    participationPercent: weightedParticipation,
                });
            }
        }
    }

    return Array.from(ownerMap.values()).sort((a, b) => b.participationPercent - a.participationPercent);
};

/** Format participation as percentage string. */
export const fmtParticipation = (value: number): string =>
    `${value.toFixed(1)}%`;

/** Colors for chart segments (up to 8 owners). */
export const CHART_COLORS = [
    '#00B6AD', // primary - sacado
    '#3B82F6', // blue
    '#8B5CF6', // purple
    '#F59E0B', // amber
    '#EF4444', // red
    '#10B981', // green
    '#6366F1', // indigo
    '#EC4899', // pink
] as const;

/** Get color for an owner in the chart (drawee always first color). */
export const getOwnerColor = (index: number, isDrawee: boolean): string => {
    if (isDrawee) return CHART_COLORS[0];
    return CHART_COLORS[Math.min(index, CHART_COLORS.length - 1)];
};
